import type { UiStreamAction } from "@intelligems/ig-types";
import isEqual from "lodash.isequal";

export interface CompletedAgenticLogicRule {
  id: string;
  managedBy: "builder" | "code";
}

export interface AgenticLogicCreationTracker {
  toolCallsById: Record<string, TrackedToolCall>;
  createdCodeRuleId?: string;
  completedRule?: CompletedAgenticLogicRule;
  rejectedRuleIds: Record<string, true>;
}

type TrackedToolCall =
  | { status: "consumed" }
  | {
      status: "pending";
      input: unknown;
      managedBy?: CompletedAgenticLogicRule["managedBy"];
      ruleId?: string;
      toolName: string;
    };

export const createAgenticLogicCreationTracker =
  (): AgenticLogicCreationTracker => ({
    rejectedRuleIds: {},
    toolCallsById: {},
  });

export function trackAgenticLogicCreation(
  tracker: AgenticLogicCreationTracker,
  action: UiStreamAction
): AgenticLogicCreationTracker {
  if (tracker.completedRule) return tracker;
  if (action.type !== "addStreamPart") return tracker;
  const part = action.streamPart;

  if (part.type === "tool-input-available") {
    const existingCall = tracker.toolCallsById[part.toolCallId];
    if (existingCall) {
      if (existingCall.status === "consumed") return tracker;
      if (
        existingCall.toolName === part.toolName &&
        isEqual(existingCall.input, part.input)
      ) {
        return tracker;
      }
      return consumeToolCall(tracker, part.toolCallId);
    }

    const ruleId = readRuleId(part.input);
    const managedBy = readManagedBy(part.input);
    return {
      ...tracker,
      toolCallsById: {
        ...tracker.toolCallsById,
        [part.toolCallId]: {
          status: "pending",
          input: part.input,
          toolName: part.toolName,
          ...(managedBy ? { managedBy } : {}),
          ...(ruleId ? { ruleId } : {}),
        },
      },
    };
  }

  if (part.type !== "tool-output-available") return tracker;
  const toolCall = tracker.toolCallsById[part.toolCallId];
  if (toolCall?.status === "consumed") return tracker;
  const consumedTracker = consumeToolCall(tracker, part.toolCallId);
  if (!toolCall) return consumedTracker;

  const results = readToolJsonResults(part.output);
  if (!results) return consumedTracker;

  if (toolCall.toolName === "create_agentic_logic_rule") {
    const rule = readCreatedRule(results);
    if (!rule) return consumedTracker;
    if (toolCall.managedBy !== rule.managedBy) {
      return {
        ...consumedTracker,
        rejectedRuleIds: {
          ...consumedTracker.rejectedRuleIds,
          [rule.id]: true,
        },
      };
    }
    if (rule.managedBy === "builder") {
      return { ...consumedTracker, completedRule: rule };
    }
    return { ...consumedTracker, createdCodeRuleId: rule.id };
  }

  if (toolCall.toolName === "save_agentic_logic_rule_draft") {
    const savedRuleId = readSavedRuleId(results);
    if (
      !toolCall.ruleId ||
      !savedRuleId ||
      toolCall.ruleId !== savedRuleId ||
      tracker.rejectedRuleIds[savedRuleId] ||
      (tracker.createdCodeRuleId && tracker.createdCodeRuleId !== savedRuleId)
    ) {
      return consumedTracker;
    }
    return {
      ...consumedTracker,
      completedRule: {
        id: savedRuleId,
        managedBy: "code",
      },
    };
  }

  return consumedTracker;
}

function consumeToolCall(
  tracker: AgenticLogicCreationTracker,
  toolCallId: string
): AgenticLogicCreationTracker {
  return {
    ...tracker,
    toolCallsById: {
      ...tracker.toolCallsById,
      [toolCallId]: { status: "consumed" },
    },
  };
}

function readManagedBy(
  value: unknown
): CompletedAgenticLogicRule["managedBy"] | null {
  if (!value || typeof value !== "object" || !("managedBy" in value)) {
    return null;
  }
  return value.managedBy === "builder" || value.managedBy === "code"
    ? value.managedBy
    : null;
}

function readRuleId(value: unknown): string | null {
  if (!value || typeof value !== "object" || !("ruleId" in value)) {
    return null;
  }
  return readNonEmptyString(value.ruleId);
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readSavedRuleId(results: Record<string, unknown>): string | null {
  if (!("draft" in results)) return null;
  return readRuleId(results.draft);
}

/**
 * Unwraps a tool result's `{type:"json", value:{results}}` envelope — the shape
 * every `SharedTool` returns, sometimes arriving as a JSON string on a replayed
 * transcript. Shared with the scenario-run tracker so both read the wire the
 * same way.
 */
export function readToolJsonResults(
  output: unknown
): Record<string, unknown> | null {
  if (!output || typeof output !== "object" || !("type" in output)) {
    return null;
  }
  if (
    output.type === "text" &&
    "value" in output &&
    typeof output.value === "string"
  ) {
    try {
      return readToolJsonResults(JSON.parse(output.value));
    } catch {
      return null;
    }
  }
  if (output.type !== "json" || !("value" in output)) return null;
  const value = output.value;
  if (!value || typeof value !== "object" || !("results" in value)) {
    return null;
  }
  const results = value.results;
  return results && typeof results === "object"
    ? (results as Record<string, unknown>)
    : null;
}

function readCreatedRule(
  results: Record<string, unknown>
): CompletedAgenticLogicRule | null {
  const rule = results.rule;
  if (!rule || typeof rule !== "object") return null;
  const fields = rule as Record<string, unknown>;
  const id = readNonEmptyString(fields.id);
  const { managedBy } = fields;
  if (!id || (managedBy !== "builder" && managedBy !== "code")) {
    return null;
  }
  return { id, managedBy };
}
